/**
 * Event Colors Utilities
 * Helper functions for event type colors and icons
 */

import {
  Calendar,
  Music,
  Mic2,
  Zap,
  Award,
  BookOpen,
  Users,
  CircleDot,
  Navigation,
} from 'lucide-react';
// =====================
// Types
// =====================

/**
 * Event type color configuration
 * When `style` is present, the color is a custom hex value and should be
 * applied via inline `style` prop instead of (or in addition to) Tailwind classes.
 */
export interface EventTypeColor {
  /** Tailwind bg class (empty string if custom hex) */
  bg: string;
  /** Tailwind light bg class (empty string if custom hex) */
  bgLight: string;
  /** Tailwind border class (empty string if custom hex) */
  border: string;
  /** Tailwind text class (empty string if custom hex) */
  text: string;
  /** Tailwind dot class (empty string if custom hex) */
  dot: string;
  /** Optional inline styles for custom hex colors — present only when a hex was provided */
  style?: {
    bg?: string;
    bgLight?: string;
    border?: string;
    text?: string;
    dot?: string;
  };
}

/**
 * Map of event types to color configurations
 */
export interface EventTypeColors {
  gig: EventTypeColor;
  rehearsal: EventTypeColor;
  recording: EventTypeColor;
  workshop: EventTypeColor;
  album_launch: EventTypeColor;
  masterclass: EventTypeColor;
  networking: EventTypeColor;
  tour: EventTypeColor;
  custom: EventTypeColor;
}

/**
 * Event color key - the string identifier for a color preset
 */
export type EventColor = string;

/**
 * Color option with display properties
 */
export interface ColorOption {
  value: EventColor;
  label: string;
  bg: string;
  bgHover: string;
}

/**
 * Map of all available color options
 */
export const EVENT_COLORS: Record<string, ColorOption> = {
  blue: { value: 'blue', label: 'Blue', bg: 'bg-blue-500', bgHover: 'hover:bg-blue-600' },
  green: { value: 'green', label: 'Green', bg: 'bg-green-500', bgHover: 'hover:bg-green-600' },
  purple: { value: 'purple', label: 'Purple', bg: 'bg-purple-500', bgHover: 'hover:bg-purple-600' },
  orange: { value: 'orange', label: 'Orange', bg: 'bg-orange-500', bgHover: 'hover:bg-orange-600' },
  red: { value: 'red', label: 'Red', bg: 'bg-red-500', bgHover: 'hover:bg-red-600' },
  yellow: { value: 'yellow', label: 'Yellow', bg: 'bg-yellow-500', bgHover: 'hover:bg-yellow-600' },
  pink: { value: 'pink', label: 'Pink', bg: 'bg-pink-500', bgHover: 'hover:bg-pink-600' },
  teal: { value: 'teal', label: 'Teal', bg: 'bg-teal-500', bgHover: 'hover:bg-teal-600' },
  indigo: { value: 'indigo', label: 'Indigo', bg: 'bg-indigo-500', bgHover: 'hover:bg-indigo-600' },
  gray: { value: 'gray', label: 'Gray', bg: 'bg-gray-500', bgHover: 'hover:bg-gray-600' },
};

/**
 * Default color mapping for each event type
 */
export const EVENT_TYPE_COLORS: Record<string, EventColor> = {
  gig: 'blue',
  rehearsal: 'green',
  recording: 'purple',
  workshop: 'orange',
  album_launch: 'red',
  masterclass: 'yellow',
  networking: 'pink',
  tour: 'indigo',
  custom: 'gray',
};

/**
 * Get event type label
 */
export function getEventTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    gig: 'Gig',
    rehearsal: 'Rehearsal',
    recording: 'Recording',
    workshop: 'Workshop',
    album_launch: 'Album Launch',
    masterclass: 'Masterclass',
    networking: 'Networking',
    tour: 'Tour',
    custom: 'Custom',
  };
  return labels[type] || 'Event';
}

/**
 * Get event icon component
 */
export function getEventIcon(type: string) {
  const icons: Record<string, React.ComponentType<any>> = {
    gig: Calendar,
    rehearsal: Music,
    recording: Mic2,
    workshop: Zap,
    album_launch: Award,
    masterclass: BookOpen,
    networking: Users,
    tour: Navigation,
    custom: CircleDot,
  };
  return icons[type] || Calendar;
}

/**
 * Map of color name to EventTypeColor object
 */
const COLOR_MAP: Record<string, EventTypeColor> = {
  blue: { bg: 'bg-blue-600', bgLight: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-600', dot: 'bg-blue-500' },
  green: { bg: 'bg-green-600', bgLight: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', dot: 'bg-green-500' },
  purple: { bg: 'bg-purple-600', bgLight: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-600', dot: 'bg-purple-500' },
  orange: { bg: 'bg-orange-600', bgLight: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600', dot: 'bg-orange-500' },
  red: { bg: 'bg-red-600', bgLight: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', dot: 'bg-red-500' },
  yellow: { bg: 'bg-yellow-600', bgLight: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-600', dot: 'bg-yellow-500' },
  pink: { bg: 'bg-pink-600', bgLight: 'bg-pink-50', border: 'border-pink-200', text: 'text-pink-600', dot: 'bg-pink-500' },
  teal: { bg: 'bg-teal-600', bgLight: 'bg-teal-50', border: 'border-teal-200', text: 'text-teal-600', dot: 'bg-teal-500' },
  indigo: { bg: 'bg-indigo-600', bgLight: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-600', dot: 'bg-indigo-500' },
  gray: { bg: 'bg-gray-600', bgLight: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', dot: 'bg-gray-500' },
};

/**
 * Check if a string is a valid hex color (e.g. #ff6b6b or #ff6b6b80)
 */
export function isHexColor(value: string): boolean {
  return /^#[0-9A-Fa-f]{3,8}$/.test(value);
}

/**
 * Convert a hex color to an rgba string with optional opacity
 */
export function hexToRgba(hex: string, alpha: number = 1): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Lighten a hex color by mixing with white at a given intensity (0-1).
 * Used to generate a "light" variant (e.g. for bgLight badges).
 */
export function lightenHex(hex: string, intensity: number = 0.85): string {
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const r = Math.round(parseInt(h.substring(0, 2), 16) + (255 - parseInt(h.substring(0, 2), 16)) * intensity);
  const g = Math.round(parseInt(h.substring(2, 4), 16) + (255 - parseInt(h.substring(2, 4), 16)) * intensity);
  const b = Math.round(parseInt(h.substring(4, 6), 16) + (255 - parseInt(h.substring(4, 6), 16)) * intensity);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Get event color configuration
 * Accepts either a preset color name ('blue', 'green') or a hex string ('#ff6b6b').
 * Returns an EventTypeColor with appropriate Tailwind classes (for presets)
 * or empty classes + inline style overrides (for hex values).
 */
export function getEventColor(
  type: string,
  customColor: string | undefined,
  colorMap: Record<string, string>
): EventTypeColor {
  let colorValue: string | undefined;

  if (customColor) {
    // customColor can be a hex like '#ff6b6b' or a preset name like 'blue'
    colorValue = customColor;
  }

  if (!colorValue) {
    colorValue = colorMap[type];
  }

  if (!colorValue) {
    colorValue = 'gray';
  }

  // If the value is a hex color, build an inline-style-driven result
  if (isHexColor(colorValue)) {
    const light = lightenHex(colorValue, 0.88);
    return {
      bg: '',
      bgLight: '',
      border: '',
      text: '',
      dot: '',
      style: {
        bg: colorValue,
        bgLight: light,
        border: colorValue,
        text: colorValue,
        dot: colorValue,
      },
    };
  }

  // Otherwise treat as a preset name
  return COLOR_MAP[colorValue] || COLOR_MAP.gray;
}

/**
 * Get status color class
 */
export function getStatusColor(status: string): string {
  const statusColors: Record<string, string> = {
    scheduled: 'bg-blue-500 border-blue-600',
    confirmed: 'bg-green-500 border-green-600',
    'in-progress': 'bg-yellow-500 border-yellow-600',
    completed: 'bg-gray-500 border-gray-600',
    cancelled: 'bg-red-500 border-red-600',
    draft: 'bg-gray-400 border-gray-500',
  };
  return statusColors[status] || 'bg-gray-500 border-gray-600';
}
